import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { BlogPost } from "@tih/shared";
import { adminDeleteBlogPost, adminFetchBlog } from "../../../api/client";
import { useEventHub } from "../../../context/EventHubContext";

export function EventBlogPanel() {
  const hub = useEventHub();
  const [rows, setRows] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminFetchBlog({ eventId: hub.eventId }));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [hub.eventId]);

  async function remove(id: number, title: string) {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await adminDeleteBlogPost(id);
    await load();
    await hub.refreshSummary();
  }

  return (
    <section className="admin-card">
      <div className="admin-page-header-row" style={{ marginBottom: 16 }}>
        <div>
          <h2 className="admin-card-title" style={{ margin: 0 }}>
            Event blog posts
          </h2>
          <p className="admin-muted" style={{ marginTop: 8 }}>
            Recaps and reflections linked to this worship night.
          </p>
        </div>
        <Link
          to={`/admin/blog/new?eventId=${hub.eventId}`}
          className="btn btn-primary"
        >
          New post for this event
        </Link>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <p className="admin-muted">
          No posts linked to this event yet. General ministry posts stay under{" "}
          <Link to="/admin/blog">Blog</Link>.
        </p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td>
                    <code>{p.slug}</code>
                  </td>
                  <td>
                    <span className={`status-pill status-${p.status.toLowerCase()}`}>
                      {p.status}
                    </span>
                  </td>
                  <td>{new Date(p.updatedAt).toLocaleString()}</td>
                  <td className="admin-row-actions">
                    <Link to={`/admin/blog/${p.id}`} className="btn btn-ghost">
                      Edit
                    </Link>
                    {p.status === "PUBLISHED" && (
                      <Link to={`/blog/${p.slug}`} className="btn btn-ghost">
                        View
                      </Link>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => void remove(p.id, p.title)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
